import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import qs from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Link, matchPath, useHistory, useLocation } from 'react-router-dom';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  Row,
} from 'reactstrap';

import Analytics from '../../../analytics/Analytics';
import useAlerts from '../../../hooks/order/useAlerts';
import globalTranslations from '../../../i18n/globalTranslations';
import ProductImageModel from '../../../models/ProductImage';
import { modelOf } from '../../../prop-types';
import AnalyticsService from '../../../services/AnalyticsService';
import RouteService from '../../../services/RouteService';
import AccountStore from '../../../store/AccountStore';
import CampaignCodeStore from '../../../store/CampaignCodeStore';
import CartStore from '../../../store/CartStore';
import ConfigStore from '../../../store/ConfigStore';
import ProductStore from '../../../store/ProductStore';
import SectionStore from '../../../store/SectionStore';
import UIStore from '../../../store/UIStore';
import Paths from '../../../types/Paths';
import ProductPdfType from '../../../types/ProductPdfType';
import RequestState from '../../../types/RequestState';
import Icon from '../../common/Icon';
import OrderTotals from '../../order/OrderTotals';
import ProductPdf from '../../product-pdf/ProductPdf';
import Price from '../../product/Price';
import ProductImage from '../../product/ProductImage';
import AlsoPurchasedProductsSlider from '../../slider/AlsoPurchasedProductsSlider';
import CartProductAlerts from '../CartProductAlerts';
import CartProductListing from '../CartProductListing';
import CartProductRow from '../CartProductRow';

let timer = null;

const CartModal = ({
  analyticsService,
  accountStore,
  cartStore,
  campaignCodeStore,
  configStore,
  productStore,
  sectionStore,
  uiStore,
  analytics,
  routeService,
  intl,
}) => {
  const { cart } = cartStore;

  const history = useHistory();
  const location = useLocation();
  const rightColumnRef = useRef(null);
  const [rightColumnHeight, setRightColumnHeight] = useState(null);
  const { renderGeneralAlerts, renderErrorAlerts } = useAlerts({
    generalAlerts: cart?.commonAlerts.general,
    errorAlerts: cart?.commonAlerts.errors,
  });
  const withTax = accountStore.showPricesWithTax;

  useEffect(() => {
    const { cartModal } = cartStore;

    const populated = handleCartPopulate();
    if (!populated) {
      maybeLoadCart();
    }

    // Close modal if user navigates to new product with AlsoPurchasedSlider
    history.listen((_) => {
      if (cartModal.isOpen) {
        cartModal.toggle();
      }
    });

    return () => {
      resetTimer();
    };
  }, []);

  const resetTimer = () => {
    timer && clearTimeout(timer);
  };

  const updateRightColumnHeight = () => {
    const rightColumnHeight = rightColumnRef
      ? rightColumnRef.clientHeight
      : 300;

    setRightColumnHeight(rightColumnHeight);
  };

  const maybeLoadCart = () => {
    if (
      cartStore.state === RequestState.NONE &&
      !!!window.isSSR &&
      !configStore.siteConfig.isShoppingCenter
    ) {
      cartStore.loadCart().then(() => {
        const thankYouPath = matchPath(location.pathname, {
          path: routeService.getPath(
            Paths.ThankYouPage,
            sectionStore.activeSection
          ),
          strict: false,
          exact: true,
        });

        if (!thankYouPath) {
          analytics.cartStatus({
            cart: cartStore.cart,
          });
        }
      });
    }
  };

  const sendAnalyticsViewCartEvent = () => {
    analyticsService.viewCartStore();
  };

  const handleOpen = () => {
    if (typeof document === 'undefined') {
      return null;
    }

    document.body.classList.add('modal-open');

    updateRightColumnHeight();
    cartStore.cartModal.isOpen && sendAnalyticsViewCartEvent();
  };

  const handleCartPopulate = () => {
    const params = qs.parse(location.search, {
      arrayFormat: 'brackets',
    });
    const { action } = params;

    if (!action || action !== 'populate') {
      return false;
    }

    const productArr = getProductsIdsFromUrl(params);

    if (productArr.length > 0) {
      cartStore.populate(productArr);
      history.replace({
        ...location,
        search: qs.stringify({}),
      });
      return true;
    }

    return false;
  };

  const getProductsIdsFromUrl = (params) => {
    const regex = /product\[(.+)\]/;
    return Object.keys(params)
      .map((param) => {
        const match = regex.exec(param);

        if (match) {
          return {
            id: match[1],
            qty: Number(params[param]),
          };
        }

        return null;
      })
      .filter((element) => element);
  };

  const changeBasket = (event) => {
    cartStore.setBasket(event.target.value);
  };

  const getNewProductTitle = () => {
    if (!cartStore.cart) {
      return null;
    }

    const addedProduct = cartStore.cart.totalAddedProduct;
    const isBundle = cartStore.cart.newProductIsBundle;

    if (isBundle) {
      return (
        <FormattedMessage
          {...globalTranslations.productBundleAddedToCartSentence}
        />
      );
    }

    return (
      <FormattedMessage
        {...globalTranslations.productAddedToCartSentence}
        values={addedProduct}
      />
    );
  };

  const createProductImage = (url) => {
    return url
      ? ProductImageModel.create({
          id: 0,
          product_id: '',
          sizes: {
            small: url,
          },
        })
      : null;
  };

  const getAlsoPurchased = () => {
    const { alsoPurchasedProductId } = cartStore;

    if (!alsoPurchasedProductId) {
      return null;
    }

    const actualId = alsoPurchasedProductId.split('{')[0];
    const product = productStore.getAnyProductById(actualId);

    if (!product) {
      return null;
    }

    return (
      <Row>
        <Col className="CartModal__also-purchased mt-2">
          <AlsoPurchasedProductsSlider
            variant="cartModal"
            activeProductId={product.id}
            product={product}
          />
        </Col>
      </Row>
    );
  };

  const getNewProductContent = () => {
    if (!cartStore.cart) {
      return null;
    }

    const newProducts = cartStore.cart?.new_products;

    return newProducts.map((product) => {
      const showUnit = !!product.stock_unit;

      return (
        <Row key={product.id} className="CartModal__new-product-row">
          <Col className="CartModal__left-column-left" xs={4}>
            <div className="CartModal__column-image">
              <ProductImage
                product={product}
                productImage={createProductImage(product.image_url)}
                size="small"
                forceAspectRatio={false}
                lazyLoading={false}
              />
            </div>
          </Col>
          <Col xs={8}>
            <div className="CartModal__column-info">
              <div className="CartModal__column-manufacturer">
                {product.manufacturer}
              </div>
              <div className="CartModal__column-name">
                <span>{product.name}</span>
                {cartStore.cart.show_product_code && (
                  <span>{product.productCode}</span>
                )}
              </div>
              {product.variations.length > 0 && (
                <div className="CartModal__column-variations">
                  {product.variations.map((variation) => (
                    <span
                      key={variation}
                      className="CartModal__column-variation"
                    >
                      {variation}
                    </span>
                  ))}
                </div>
              )}
              {product.hasPrice && (
                <div className="CartModal__column-price">
                  <Price price={product.price_info.getPrice(withTax)} />
                  {showUnit && (
                    <span className="CartModal__stock-unit">
                      {' '}
                      / {product.stock_unit}
                    </span>
                  )}
                </div>
              )}
            </div>
          </Col>
        </Row>
      );
    });
  };

  const renderPriceInformation = () => {
    const { cart } = cartStore;

    return (
      <div className="CartModal__price-information">
        <OrderTotals totals={cart.allTotals} />
      </div>
    );
  };

  const onClickHandler = () => {
    cartStore.toggleCartButton();

    timer = setTimeout(() => {
      cartStore.closeModal();
      cartStore.toggleCartButton();
    }, 1700);

    sendAnalyticsCheckoutEvent();
  };

  const sendAnalyticsCheckoutEvent = () => {
    if (!configStore.gtm.enabled) {
      return;
    }

    let coupon;
    const activeCoupon = campaignCodeStore.activeCoupon;
    if (activeCoupon && !activeCoupon.hasProductLimitations()) {
      coupon = activeCoupon.code;
    }

    const cartProducts = cartStore.cart?.products || [];
    const actionField = { step: 1, option: 'Move To Checkout' };
    const value = cartStore.cart?.productsTotal?.value || 0;
    analytics.checkout(cartProducts, actionField, value, coupon);
  };

  const pdfCatalogEnabled = () => {
    const { showPdfButton } = configStore.cart.modal;

    if (showPdfButton === '0' || cartStore.cart.products.length === 0) {
      return false;
    }
    if (
      showPdfButton === '1' ||
      (showPdfButton === '2' && accountStore.loggedIn)
    ) {
      return true;
    }
    return showPdfButton === '3' && accountStore.isRetailer;
  };

  const getCheckoutButtonContent = () => {
    return cartStore.cartButtonDisabled ? (
      <FormattedMessage {...globalTranslations.loading} />
    ) : (
      <FormattedMessage {...globalTranslations.proceedToCheckoutSentence} />
    );
  };

  const getCartContent = () => {
    const { checkout } = configStore;
    const { showImages } = configStore.cart.modal;

    const showLeftColumn = !cartStore.cartModal.openedManually;
    const showProductListingColumn =
      cartStore.cartModal.openedManually &&
      showImages &&
      cart.products.length > 0;
    const leftColumnActive = showLeftColumn || showProductListingColumn;
    const showHigherButton = showLeftColumn && uiStore.isMobile;
    const openedManually = cartStore.cartModal.openedManually;

    const continueShoppingButton = (
      <div className="CartModal__continue">
        <Button
          color="secondary"
          onClick={cartStore.cartModal.toggle}
          size="lg"
          block
        >
          <Icon className="CartModal__continue-icon" name="angle-double-left" />
          <FormattedMessage
            id="cart.continueShopping"
            defaultMessage="Continue shopping"
          />
        </Button>
      </div>
    );

    const checkoutButton =
      checkout.checkoutPath === Paths.LiteCheckout ? (
        <div className="CartModal__checkout">
          <Button
            block
            color="primary"
            tag="a"
            href={checkout.checkoutPath}
            rel="noopener"
            size="lg"
            onClick={onClickHandler}
            aria-label={intl.formatMessage(
              globalTranslations.proceedToCheckoutSentence
            )}
            disabled={cartStore.cartButtonDisabled}
          >
            <Icon className="CartModal__checkout-icon" name="shopping-cart" />
            {getCheckoutButtonContent()}
          </Button>
        </div>
      ) : (
        <div className="CartModal__checkout">
          <Link to={Paths.Checkout}>
            <Button
              block
              color="primary"
              size="lg"
              onClick={onClickHandler}
              aria-label={intl.formatMessage(
                globalTranslations.proceedToCheckoutSentence
              )}
              disabled={cartStore.cartButtonDisabled}
            >
              <Icon className="CartModal__checkout-icon" name="shopping-cart" />
              {getCheckoutButtonContent()}
            </Button>
          </Link>
        </div>
      );

    // TODO: Change after coupons have been implemented
    const activeCoupons = true;
    const selectBasketElementId = 'CartModal__select-basket';
    const productListHeight = `${rightColumnHeight}px`;

    return (
      <>
        <Row>
          {cart.commonAlerts.general.length > 0 && (
            <CartProductAlerts alerts={renderGeneralAlerts()} />
          )}
          {showLeftColumn && (
            <Col
              xs="12"
              md="6"
              className={classNames(
                'CartModal__column CartModal__left-column',
                {
                  'CartModal__left-column-with-coupons-compensation':
                    activeCoupons,
                }
              )}
            >
              <h4 className="CartModal__title">{getNewProductTitle()}</h4>
              {cart.commonAlerts.errors.length > 0 && (
                <Row>
                  <CartProductAlerts alerts={renderErrorAlerts()} />
                </Row>
              )}
              <div
                className="CartModal__column-inner"
                style={{ maxHeight: productListHeight }}
              >
                {getNewProductContent()}
                {showProductListingColumn && (
                  <div
                    className="CartModal__product-list--with-images"
                    style={{ maxHeight: productListHeight }}
                  >
                    {cart.products.map((product) => (
                      <CartProductRow
                        // We can have multiple product bundles with the same main product.
                        // Causes React duplicate key error in console.
                        key={product.bundle_id || product.id}
                        product={product}
                        withTax={withTax}
                      />
                    ))}
                  </div>
                )}
              </div>
            </Col>
          )}
          {showProductListingColumn && (
            <CartProductListing
              activeCoupons={activeCoupons}
              showProductListingColumn={showProductListingColumn}
              productListHeight={productListHeight}
              withTax={withTax}
            />
          )}
          <Col
            xs={12}
            md={leftColumnActive ? 6 : 12}
            className="CartModal__right-column CartModal__column"
          >
            {showHigherButton && checkoutButton}
            <h4 className="CartModal__title">
              <FormattedMessage {...globalTranslations.cartContents} />
            </h4>
            <div className="CartModal__column-inner" ref={rightColumnRef}>
              <div>
                <span className="CartModal__cart-product-count">
                  {cart && cart.baskets_and_templates.length > 0 && (
                    <FormGroup>
                      <Label for={selectBasketElementId}>
                        <FormattedMessage
                          id="cart.selectShoppingCart"
                          defaultMessage="Select shopping cart"
                        />
                      </Label>
                      <Input
                        type="select"
                        onChange={changeBasket}
                        id={selectBasketElementId}
                        value={cart.selected_basket_id}
                      >
                        {cart.baskets_and_templates.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.text}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  )}
                  {(!cart || cart.number_of_products === 0) && (
                    <FormattedMessage
                      {...globalTranslations.cartEmptySentence}
                    />
                  )}
                  {cart && cart.number_of_products > 0 && (
                    <FormattedMessage
                      {...globalTranslations.itemsInCart}
                      values={{
                        itemCount: (
                          <FormattedMessage
                            {...globalTranslations.itemCount}
                            values={{
                              count: cart.number_of_products,
                            }}
                          />
                        ),
                      }}
                    />
                  )}
                </span>
                {renderPriceInformation()}
              </div>
              {pdfCatalogEnabled() && (
                <ProductPdf
                  activeId={cart.selected_basket_id.toString()}
                  pdfType={ProductPdfType.CART}
                />
              )}
              <div>
                {!showHigherButton && checkoutButton}
                {activeCoupons && (
                  <div className="CartModal__coupon-instructions">
                    <FormattedMessage
                      id="cart.couponInstructions"
                      defaultMessage="Discount coupons may be provided on the checkout page."
                    />
                  </div>
                )}
                {continueShoppingButton}
              </div>
            </div>
          </Col>
        </Row>
        {showLeftColumn &&
          !openedManually &&
          cartStore.state === RequestState.LOADED &&
          configStore.productPage.apiAlsoPurchased &&
          getAlsoPurchased()}
      </>
    );
  };

  const toggleCartModal = () => cartStore.cartModal.toggle();

  const { showImages } = configStore.cart.modal;
  if (!cartStore.cart) {
    return null;
  }

  return (
    <Modal
      className={classNames('CartModal', {
        'CartModal__after-add-to-cart':
          !cartStore.cartModal.openedManually ||
          (showImages && cartStore.cart.products.length > 0),
      })}
      size="lg"
      onOpened={handleOpen}
      isOpen={cartStore.cartModal.isOpen}
      toggle={cartStore.cartModal.toggle}
    >
      <div className="CartModal__hanging-buttons">
        <Button onClick={toggleCartModal.bind(this)} color="primary">
          <span className="CartModal__close-button">
            <Icon className="CartModal__close-icon" name="times" />
            <FormattedMessage {...globalTranslations.closeTitle} />
          </span>
        </Button>
      </div>
      <ModalBody>{getCartContent()}</ModalBody>
    </Modal>
  );
};

CartModal.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  campaignCodeStore: modelOf(CampaignCodeStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  sectionStore: modelOf(SectionStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  analyticsService: PropTypes.instanceOf(AnalyticsService).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(
  inject(
    'analyticsService',
    'accountStore',
    'cartStore',
    'campaignCodeStore',
    'configStore',
    'sectionStore',
    'productStore',
    'uiStore',
    'analytics',
    'routeService'
  )(observer(CartModal))
);
